<template>
  <div v-if="showLargePreview && fileToPreview" class="m-file-input-preview">
    <div v-if="previewLoading" class="m-file-input-preview-status">Загрузка превью…</div>
    <div
      v-else-if="previewError"
      class="m-file-input-preview-status m-file-input-preview-status--error"
    >
      {{ previewError }}
    </div>
    <div
      v-else-if="previewKind === 'pdf' && previewDisplayUrl"
      ref="pdfHostRef"
      class="pdf"
      :style="{ minHeight: `${pdfMinHeightPx}px` }"
    >
      <iframe :src="previewDisplayUrl" frameborder="0" title="PDF preview" />
    </div>
    <div v-else-if="previewKind === 'image' && previewDisplayUrl" class="image">
      <img :src="previewDisplayUrl" :alt="getFileName(fileToPreview)" />
    </div>
    <div v-else-if="previewKind === 'office' && officeInlinePreviewEnabled" class="r7-inline-host office">
      <div
        v-if="r7InlinePreviewHostId"
        v-show="!hideR7InlinePreviewMount"
        :id="r7InlinePreviewHostId"
        class="r7-inline-host__mount"
      />
      <div
        v-if="showR7InlinePreviewOverlay"
        class="r7-inline-host__overlay m-file-input-preview-status"
      >
        {{ r7InlinePreviewOverlayText }}
      </div>
      <div
        v-else-if="r7InlinePreviewError"
        class="r7-inline-host__overlay m-file-input-preview-status m-file-input-preview-status--error"
      >
        {{ r7InlinePreviewError }}
      </div>
    </div>
    <div v-else class="m-file-input-preview-placeholder">
      <i class="pi pi-file" />
      <p>{{ previewPlaceholderText }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FileItem } from '@/common/types/fileItem'
import {
  computeR7EditorIframeHeightPx,
  R7_EDITOR_MIN_HEIGHT_PX,
} from '@/common/utils/resolveR7EditorIframeHeight'
import { ref, watch } from 'vue'

defineProps<{
  showLargePreview: boolean
  fileToPreview: FileItem | null
  previewLoading: boolean
  previewError: string | null
  previewKind: string
  previewDisplayUrl: string | null
  officeInlinePreviewEnabled: boolean
  r7InlinePreviewHostId: string
  hideR7InlinePreviewMount: boolean
  showR7InlinePreviewOverlay: boolean
  r7InlinePreviewOverlayText: string
  r7InlinePreviewError: string | null
  previewPlaceholderText: string
  getFileName: (file: FileItem) => string
}>()

const pdfMinHeightPx = R7_EDITOR_MIN_HEIGHT_PX
const pdfHostRef = ref<HTMLElement | null>(null)

watch(pdfHostRef, (el, _prev, onCleanup) => {
  if (!el) return

  const apply = () => {
    const next = `${computeR7EditorIframeHeightPx({ editorWidthPx: el.offsetWidth })}px`
    if (el.style.height !== next) el.style.height = next
  }

  apply()
  const observer = new ResizeObserver(apply)
  observer.observe(el)
  onCleanup(() => observer.disconnect())
})
</script>

<style scoped lang="scss">
.m-file-input-preview {
  margin-top: 20px;

  .m-file-input-preview-status {
    padding: 24px;
    text-align: center;
    color: #6b7280;

    &--error {
      color: #e24c4c;
    }
  }

  .m-file-input-preview-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 200px;
    padding: 24px;
    text-align: center;
    color: #6b7280;
    background: var(--surface-b);
    border-radius: var(--border-radius);

    i {
      font-size: 2.5rem;
    }
  }

  .pdf {
    width: 100%;
    aspect-ratio: 3 / 2;
    background: var(--surface-b);
    border-radius: var(--border-radius);

    iframe {
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }
  .image {
    display: flex;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 80vh;
      object-fit: contain;
    }
  }

  .r7-inline-host {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 480px;
    aspect-ratio: 3 / 2;
    background: var(--surface-b);
    border-radius: var(--border-radius);

    &__mount {
      flex: 1;
      min-height: 0;
      width: 100%;
      height: 100%;
    }

    &__overlay {
      position: absolute;
      inset: 0;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      text-align: center;
      background: var(--surface-b);
      border-radius: var(--border-radius);
    }
  }
}
</style>
